import { PatientFlag } from '@seen/core/models/flags/patientFlag'
import * as patients from '@seen/core/models/patients'
import { createReferenceStringWithHistory } from '@seen/core/references'
import { groupBy, orderBy } from 'lodash-es'
import type { ReactNode } from 'react'
import { ActivityEntry } from '#src/components/activity/ActivityEntry.tsx'
import { formatCriticalAttentionSetOn } from '#src/components/critical-attention/criticalAttentionFlag.ts'
import { versionsForOosaHistory } from '#src/components/oosa/UseOosaFlagActivity.tsx'
import { useBulkResourceVersions } from '#src/hooks/useResourceVersions.ts'

export function useCriticalAttentionFlagActivity(
  flagIds: readonly string[] | undefined,
  fallbackFlags: readonly PatientFlag[] = []
): ReactNode[] {
  const uniqueIds = [...new Set(flagIds ?? [])].filter(Boolean)
  const { data: historyVersions = [] } = useBulkResourceVersions(
    PatientFlag,
    uniqueIds
  )

  const historyById = groupBy(
    historyVersions,
    (version) => version.toFhirResource().id ?? ''
  )
  const fallbackById = groupBy(
    fallbackFlags,
    (flag) => flag.toFhirResource().id ?? ''
  )
  const described = [...new Set([...uniqueIds, ...Object.keys(fallbackById)])]
    .filter(Boolean)
    .flatMap((flagId) => {
      const fallback = fallbackById[flagId]?.[0]
      return describeCriticalAttentionFlagVersions(
        versionsForOosaHistory(historyById[flagId], fallback)
      )
    })

  return orderBy(
    described,
    [(entry) => versionTime(entry.version)],
    ['desc']
  ).map(({ version, previous, action }) => {
    const fhir = version.toFhirResource()
    return (
      <ActivityEntry
        key={
          fhir.meta?.versionId
            ? createReferenceStringWithHistory(fhir)
            : (fhir.id ?? action)
        }
        author={version.resourceAuthor}
        time={versionTime(version) || undefined}
        action={action}
      >
        {version.oosaNotes && version.oosaNotes !== previous?.oosaNotes && (
          <div className="rounded-md bg-muted px-2 py-1 text-xs whitespace-pre-wrap text-foreground-secondary">
            {version.oosaNotes}
          </div>
        )}
      </ActivityEntry>
    )
  })
}

interface DescribedCriticalAttentionFlagVersion {
  version: PatientFlag
  previous: PatientFlag | undefined
  action: string
}

function describeCriticalAttentionFlagVersions(
  versions: readonly PatientFlag[]
): DescribedCriticalAttentionFlagVersion[] {
  const ascending = orderBy(
    versions,
    [(version) => versionTime(version)],
    ['asc']
  )
  const entries: DescribedCriticalAttentionFlagVersion[] = []
  ascending.forEach((version, index) => {
    const previous = index > 0 ? ascending[index - 1] : undefined
    const action = describeCriticalAttentionFlagChange(previous, version)
    if (!action) {
      return
    }
    entries.push({ version, previous, action })
  })
  return entries
}

export function describeCriticalAttentionFlagChange(
  previous: PatientFlag | undefined,
  current: PatientFlag
): string | undefined {
  if (!previous) {
    const setOn = current.criticalAttentionDetails?.setOn ?? current.start
    return setOn
      ? `created the Critical Attention flag (${formatCriticalAttentionSetOn(setOn)})`
      : 'created the Critical Attention flag'
  }

  if (previous.status !== current.status) {
    if (current.status === 'inactive') {
      return 'inactivated the Critical Attention flag'
    }
    if (current.status === 'active') {
      return 'reactivated the Critical Attention flag'
    }
  }

  if (previous.reason !== current.reason) {
    const reason =
      current.criticalAttentionDetails != null
        ? patients.formatCriticalAttentionReasonDisplay(
            current.criticalAttentionDetails.reason
          )
        : current.reason
    return reason ? `updated the reason to ${reason}` : 'cleared the reason'
  }

  if (previous.oosaNotes !== current.oosaNotes) {
    return current.oosaNotes ? 'added a note' : 'cleared the note'
  }

  return undefined
}

function versionTime(flag: PatientFlag): string {
  return flag.toFhirResource().meta?.lastUpdated ?? ''
}
